// Ads Analytics — the PRD's six sub-features.
//
// Six views, one engine: the same number never differs between two pages,
// because every figure on every view comes from the same summed rows through
// the same formulas.
//
// Diagnostic only. Nothing here changes a bid, a budget, a keyword or a
// campaign — the labels are verdicts, and the actions they imply are taken in
// the Amazon console or in the optimisers.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiLink } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './AdsAnalytics.module.css';
import AuthContext from '../../../context/AuthContext';
import FeatureCards from './components/FeatureCards';
import Performance from './components/Performance';
import Drill from './components/Drill';
import Products from './components/Products';
import Placements from './components/Placements';
import SearchTerms from './components/SearchTerms';
import Alerts from './components/Alerts';
import { NotConnectedError, getAlerts, getDrill, getOverview, getPerformance,
         getPlacements, getProducts, getSearchTerms } from './api';

const TABS = [
  { id: 'overview', label: 'Performance', ref: 'SF1' },
  { id: 'drill', label: 'Campaigns', ref: 'SF2' },
  { id: 'products', label: 'Products', ref: 'SF3' },
  { id: 'placements', label: 'Placements', ref: 'SF4' },
  { id: 'terms', label: 'Search terms', ref: 'SF5' },
  { id: 'alerts', label: 'Alerts', ref: 'SF6' },
];
const RANGES = [[7, 'Last 7 days'], [14, 'Last 14 days'], [30, 'Last 30 days'],
                [60, 'Last 60 days']];

export default function AdsAnalytics() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [tab, setTab] = useState('overview');
  const [days, setDays] = useState(30);
  const [grain, setGrain] = useState('day');
  const [level, setLevel] = useState('campaigns');
  const [parent, setParent] = useState(null);
  const [filter, setFilter] = useState('');
  const [termView, setTermView] = useState('all');

  const [overview, setOverview] = useState(null);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(null);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(e.message);
    else setError(e.message);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    getOverview(token, { days })
      .then((r) => { if (live) { setOverview(r); setNotConnected(null); } })
      .catch((e) => { if (live) fail(e); });
    return () => { live = false; };
  }, [token, days, fail]);

  const load = useCallback(async () => {
    if (!token || tab === 'overview') return;
    setLoading(true); setError(null);
    try {
      if (tab === 'drill') setView(await getDrill(token, { days, level, parent, label: filter }));
      else if (tab === 'products') setView(await getProducts(token, { days }));
      else if (tab === 'placements') setView(await getPlacements(token, { days }));
      else if (tab === 'terms') setView(await getSearchTerms(token, { days, view: termView }));
      else if (tab === 'alerts') setView(await getAlerts(token, { days }));
    } catch (e) { fail(e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tab, days, level, parent, filter, termView]);

  useEffect(() => { load(); }, [load]);

  const [performance, setPerformance] = useState(null);
  useEffect(() => {
    if (!token || tab !== 'overview') return;
    getPerformance(token, { days, grain }).then(setPerformance).catch(fail);
  }, [token, tab, days, grain, fail]);

  function body() {
    if (notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Amazon Ads is not connected</h2>
          <p className={s.lockedBody}>
            {notConnected} Advertising is a second Amazon authorisation, separate from the
            Selling Partner one — connect it on the Amazon card and collect the last 60 days.
            Nothing here is shown as zero in the meantime.
          </p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Go to the Amazon card
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}

        <section className={s.panel}>
          <div className={s.contextBar}>
            <div className={s.contextLeft}>
              <label className={s.field}>
                <span className={s.label}>Period</span>
                <select className={s.select} value={days}
                        onChange={(e) => setDays(Number(e.target.value))}>
                  {RANGES.map(([n, label]) => <option key={n} value={n}>{label}</option>)}
                </select>
              </label>
            </div>
            {overview && (
              <div className={s.coverage}>
                {overview.attribution}
                {' '}Target ACOS {(overview.target_acos * 100).toFixed(0)}%
                {overview.target_source === 'benchmark' ? ' (a benchmark, not a target you set)' : ''}.
              </div>
            )}
          </div>
        </section>

        <FeatureCards data={overview} loading={!overview}
                      onOpen={(next) => setTab(next === 'alerts' ? 'alerts' : next)} />

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => { setTab(t.id); setParent(null); }}>
                {t.label}<span className={s.tabRef}>{t.ref}</span>
              </button>
            ))}
          </div>

          <div style={{ paddingTop: 16 }}>
            {tab === 'overview' && (
              <Performance data={performance} loading={!performance}
                           grain={grain} onGrain={setGrain} />
            )}
            {tab === 'drill' && (
              <Drill data={view} loading={loading} level={level} parent={parent} filter={filter}
                     onLevel={(l) => { setLevel(l); setParent(null); }}
                     onFilter={setFilter}
                     onBack={() => setParent(null)}
                     onOpen={(id) => {
                       setParent(id);
                       setLevel(level === 'campaigns' ? 'ad_groups'
                         : level === 'ad_groups' ? 'targets' : 'search_terms');
                     }} />
            )}
            {tab === 'products' && <Products data={view} loading={loading} />}
            {tab === 'placements' && <Placements data={view} loading={loading} />}
            {tab === 'terms' && (
              <SearchTerms data={view} loading={loading} view={termView} onView={setTermView} />
            )}
            {tab === 'alerts' && <Alerts data={view} loading={loading} />}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Ads Analytics</h1></div>
          <p>Whether your ads make money, what is costing you, and what changed</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
